/**
 * Singleton toast store. `showToast()` is callable from anywhere (event
 * handlers, fetch error branches, etc.) without prop-drilling a context. The
 * `<ToastHost />` component subscribes and renders.
 */
import { useEffect, useState } from 'hono/jsx';

export type ToastKind = 'success' | 'error' | 'info' | 'warning';
export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  message: string;
}

let nextId = 1;
let current: Toast | null = null;
let timer: number | null = null;
const listeners = new Set<() => void>();

function notify() { listeners.forEach((l) => l()); }

export function showToast(kind: ToastKind, title: string, message: string): void {
  current = { id: nextId++, kind, title, message };
  const myId = current.id;
  notify();
  if (timer) { clearTimeout(timer); timer = null; }
  timer = setTimeout(() => {
    if (current && current.id === myId) { current = null; notify(); }
  }, 4000) as unknown as number;
}

export function dismissToast(): void {
  if (timer) { clearTimeout(timer); timer = null; }
  current = null;
  notify();
}

/** Hook that re-renders the host whenever the current toast changes. */
export function useCurrentToast(): Toast | null {
  const [, bump] = useState<number>(0);
  useEffect(() => {
    const l = () => bump((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return current;
}
